/**
 * Client-side filters for Full Inventory Report lines (pure helpers).
 */

export function lineHasFlags(line) {
  return !!(
    line &&
    (line.isPriorityZero ||
      line.isHighValue ||
      (Array.isArray(line.flagTypes) && line.flagTypes.length > 0))
  );
}

const CHANGED_DIRECTIONS = new Set([
  'increased',
  'decreased',
  'new',
  'missing',
  'added',
  'removed'
]);

export function lineHasMovement(line, directions) {
  const wanted = directions instanceof Set ? directions : new Set(directions || []);
  if (!line || wanted.size === 0) return false;
  if (wanted.has(line.direction)) return true;
  const summary = line.siteMovementSummary || {};
  for (const direction of wanted) {
    if ((Number(summary[direction]) || 0) > 0) return true;
  }
  return (line.siteBreakdown || []).some((site) => wanted.has(site && site.movement));
}

/**
 * @param {array} lines
 * @param {object} filters
 * @returns {array}
 */
export function filterFullInventoryLines(lines, filters = {}) {
  const list = Array.isArray(lines) ? lines : [];
  const search = (filters.search || '').trim().toLowerCase();
  const brand = (filters.brand || '').trim().toLowerCase();
  const category = (filters.category || '').trim().toLowerCase();
  const flagsOnly = !!filters.flagsOnly;
  const changedOnly = !!filters.changedOnly;
  const increasedOnly = !!filters.increasedOnly;
  const decreasedOnly = !!filters.decreasedOnly;
  const newMissingOnly = !!filters.newMissingOnly;
  const addedOnly = !!filters.addedOnly;
  const removedOnly = !!filters.removedOnly;
  const highVarianceOnly = !!filters.highVarianceOnly;
  const priorityOnly = !!filters.priorityOnly;

  return list.filter((line) => {
    if (brand && (line.brandName || '').toLowerCase() !== brand) return false;
    if (category && (line.category || '').toLowerCase() !== category) return false;
    if (flagsOnly && !lineHasFlags(line)) return false;
    if (priorityOnly && !line.priority) return false;
    if (highVarianceOnly && !line.highVariance) return false;

    if (increasedOnly && !lineHasMovement(line, ['increased'])) return false;
    if (decreasedOnly && !lineHasMovement(line, ['decreased'])) return false;
    if (newMissingOnly && !lineHasMovement(line, ['new', 'missing'])) return false;
    if (addedOnly && !lineHasMovement(line, ['added'])) return false;
    if (removedOnly && !lineHasMovement(line, ['removed'])) return false;
    if (changedOnly) {
      // Preserve single-count behavior. Compare rows match aggregate or site-level movement.
      if (line.direction != null && !lineHasMovement(line, CHANGED_DIRECTIONS)) return false;
    }

    if (search) {
      const hay = [
        line.itemName,
        line.itemDescription,
        line.vendor,
        line.vendorItemCode,
        line.brandName,
        line.category,
        line.packSize,
        line.location,
        line.notes,
        line.notesCount1,
        line.notesCount2,
        line.managerAckComment,
        line.managerAckComment1,
        line.managerAckComment2
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

export function uniqueSortedField(lines, field) {
  const set = new Set();
  for (const line of lines || []) {
    const v = (line[field] || '').trim();
    if (v) set.add(v);
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function buildValueByGroup(lines, groupField, mode) {
  const map = new Map();
  for (const line of lines || []) {
    const label = (line[groupField] || '').trim() || (groupField === 'brandName' ? 'Unbranded' : 'Uncategorized');
    if (!map.has(label)) map.set(label, { label, count1: 0, count2: 0, value: 0 });
    const row = map.get(label);
    if (mode === 'compare') {
      row.count1 += Number(line.count1?.lineValue) || 0;
      row.count2 += Number(line.count2?.lineValue) || 0;
    } else {
      row.value += Number(line.lineValue) || 0;
    }
  }
  const arr = [...map.values()];
  arr.sort((a, b) => {
    const av = mode === 'compare' ? Math.max(a.count1, a.count2) : a.value;
    const bv = mode === 'compare' ? Math.max(b.count1, b.count2) : b.value;
    return bv - av;
  });
  return arr.slice(0, 12);
}

export function buildMovementBreakdown(summary) {
  if (!summary) return [];
  return [
    { label: 'Increased', value: Number(summary.linesIncreased) || 0, key: 'increased' },
    { label: 'Decreased', value: Number(summary.linesDecreased) || 0, key: 'decreased' },
    { label: 'Unchanged', value: Number(summary.linesUnchanged) || 0, key: 'unchanged' },
    { label: 'New', value: Number(summary.linesNew) || 0, key: 'new' },
    { label: 'Missing', value: Number(summary.linesMissing) || 0, key: 'missing' },
    { label: 'Added', value: Number(summary.linesAdded) || 0, key: 'added' },
    { label: 'Removed', value: Number(summary.linesRemoved) || 0, key: 'removed' }
  ];
}

export function buildSiteMovementBreakdown(lines) {
  const counts = {
    increased: 0,
    decreased: 0,
    unchanged: 0,
    new: 0,
    missing: 0,
    added: 0,
    removed: 0
  };
  for (const line of lines || []) {
    for (const site of line.siteBreakdown || []) {
      if (site && Object.prototype.hasOwnProperty.call(counts, site.movement)) {
        counts[site.movement] += 1;
      }
    }
  }
  return [
    { label: 'Increased', value: counts.increased, key: 'increased' },
    { label: 'Decreased', value: counts.decreased, key: 'decreased' },
    { label: 'Unchanged', value: counts.unchanged, key: 'unchanged' },
    { label: 'New', value: counts.new, key: 'new' },
    { label: 'Missing', value: counts.missing, key: 'missing' },
    { label: 'Added', value: counts.added, key: 'added' },
    { label: 'Removed', value: counts.removed, key: 'removed' }
  ];
}

export function buildTopVariances(lines, limit = 10) {
  const list = (lines || [])
    .filter((l) => l && l.difference && l.difference.value != null)
    .map((l) => ({
      itemName: l.itemName || '—',
      vendorItemCode: l.vendorItemCode || '',
      valueDiff: Number(l.difference.value) || 0,
      variancePct: l.difference.variancePct,
      direction: l.direction
    }))
    .filter((l) => Math.abs(l.valueDiff) > 1e-9);
  list.sort((a, b) => Math.abs(b.valueDiff) - Math.abs(a.valueDiff));
  return list.slice(0, limit);
}

/**
 * Format quantity1 / quantity2 for display. Blank/null → 0. Never adds the quantities.
 */
export function formatQuantityDisplay(quantity1, quantity2) {
  const toQty = (val) => {
    if (val === undefined || val === null || val === '') return 0;
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };
  return `${toQty(quantity1)} / ${toQty(quantity2)}`;
}

/**
 * Resolve compare-side quantity display from payload or raw fields.
 */
export function resolveCountQuantityDisplay(side) {
  if (!side) return null;
  if (side.quantityDisplay) return side.quantityDisplay;
  return formatQuantityDisplay(side.countedQuantity, side.countedQuantity2);
}

/** Tenant-friendly explanations for comparison movement flags. */
export const MOVEMENT_HELP_ITEMS = [
  {
    key: 'added',
    label: 'Added',
    text: 'item appears in Count 2 but was not on the Count 1 sheet.'
  },
  {
    key: 'removed',
    label: 'Removed',
    text: 'item appears in Count 1 but was not on the Count 2 sheet.'
  },
  {
    key: 'new',
    label: 'New',
    text: 'item appears on both count sheets but was zero in Count 1 and counted in Count 2.'
  },
  {
    key: 'missing',
    label: 'Missing',
    text: 'item appears on both count sheets but was counted in Count 1 and zero in Count 2.'
  },
  {
    key: 'increased',
    label: 'Increased',
    text: 'item appears on both count sheets and Count 2 value is higher.'
  },
  {
    key: 'decreased',
    label: 'Decreased',
    text: 'item appears on both count sheets and Count 2 value is lower.'
  },
  {
    key: 'unchanged',
    label: 'Unchanged',
    text: 'item appears on both count sheets and values are the same.'
  }
];

export const ZERO_ROW_CAVEAT_TEXT =
  'Added and Removed are based on whether an item appears in each count sheet. If a count did not save zero rows, some zero-count items may appear as Added or Removed.';
